"use client";

import React, { useState, useEffect } from "react";
import { observer } from "mobx-react-lite";
import { useStore } from "../services/store";
import Iframe from "react-iframe";
import { getHistFromDate, goPrevHist, goNextHist } from "./util-tab";
import { addWatch, setSearchParam, formatDate } from "./util-api";
import NewsSummaryButton from "./NewsSummaryButton";
import EarningsSnapshot from "./EarningsSnapshot";
import { getSectorBySymbol, getSectorsBySymbol } from "./symbols";
import SymbolChart from "./SymbolChart";
import OptionsCacheTable from "./OptionsCacheTable";
import { Toast } from "./Toast";
import * as d3 from "d3";

const SymbolIframe = observer(function SymbolIframe({
  symbol,
  maxWidth = 1000,
  containerStyle = {},
  date: inputDate,
  pattern: propPattern,
}) {
  const store = useStore();

  const [newsOpen, setNewsOpen] = useState(false);
  const [newsData, setNewsData] = useState(null);
  const [earningsSnapshot, setEarningsSnapshot] = useState(null);

  const [optionsOpen, setOptionsOpen] = useState(false);
  const [optionsData, setOptionsData] = useState(null);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [optionsErr, setOptionsErr] = useState(null);
  const [optionsTerm, setOptionsTerm] = useState("long");
  const [toastMsg, setToastMsg] = useState(null);
  const [toastType, setToastType] = useState("success");
  const [favoriteSaving, setFavoriteSaving] = useState(false);

  // Chart crosshair tooltip data
  const [crosshairData, setCrosshairData] = useState(null);

  // Chart navigation state
  const [chartTicks, setChartTicks] = useState([]);
  const [currentTickIndex, setCurrentTickIndex] = useState(-1);

  // Signal history expansion state
  const [signalHistoryExpanded, setSignalHistoryExpanded] = useState(false);

  // Pattern expansion state
  const [patternExpanded, setPatternExpanded] = useState(false);

  // Tooltip visibility state
  const [tooltipVisible, setTooltipVisible] = useState(false);

  const handleCrosshairMove = (data) => {
    setCrosshairData(data);
  };

  const marketCap = store.marketCapMap?.[symbol];
  const pattern = propPattern || store.patternMap?.[symbol];
  const curTick = store.gridMap?.[symbol]?.[0];
  const erVal = store.erMap?.[symbol];
  const s = (symbol || "").toLowerCase();
  const isFavorite = store.favoriteList.some(
    (item) => item.toLowerCase() === s,
  );
  const inPosSignals = store.signalPosSymbols?.has(s);
  const inNegSignals = store.signalNegSymbols?.has(s);
  const inSignals = inPosSignals || inNegSignals;

  const historySignals = store.getSignalHistoryForSymbol(symbol);

  // Load chart data for navigation
  useEffect(() => {
    if (!symbol) return;

    const yearStr = store.hist ? `-${store.hist}` : "";
    const filePath = `/csv-hist/csv${yearStr}/${symbol}.prr.csv`;

    fetch(filePath)
      .then((r) => r.text())
      .then((csvText) => {
        const parsed = d3.csvParseRows(csvText.trim());
        const ticks = parsed.filter((row) => row.length > 0);
        setChartTicks(ticks);

        // Find current index based on highlighted date
        const highlightDate =
          store.signalContextDate || inputDate || store.formattedDate;
        const index = ticks.findIndex((tick) => tick[0] === highlightDate);
        setCurrentTickIndex(index >= 0 ? index : ticks.length - 1);
      })
      .catch((err) => {
        console.error("Failed to load chart data:", err);
        setChartTicks([]);
        setCurrentTickIndex(-1);
      });
  }, [
    symbol,
    store.hist,
    store.signalContextDate,
    inputDate,
    store.formattedDate,
  ]);

  // Helper function to get tick description text
  const getPropVal = (tick, prop) => {
    let attr = tick.find((item) => item.indexOf(prop + "=") === 0);
    if (attr === undefined) return undefined;
    return attr.split("=")[1];
  };

  // Navigation functions
  const handleChartLeft = () => {
    if (currentTickIndex < chartTicks.length - 1) {
      const newIndex = currentTickIndex + 1;
      const tick = chartTicks[newIndex];
      setCurrentTickIndex(newIndex);

      // Update tooltip with new data
      const tooltipData = {
        date: tick[0],
        open: Number(tick[3]).toFixed(2),
        high: Number(tick[2]).toFixed(2),
        low: Number(tick[1]).toFixed(2),
        close: Number(tick[4]).toFixed(2),
        text: getPropVal(tick, "description") || "",
      };
      setCrosshairData(tooltipData);

      // Update chart highlight
      store.setSignalContextDate(tick[0]);
    }
  };

  const handleChartRight = () => {
    if (currentTickIndex > 0) {
      const newIndex = currentTickIndex - 1;
      const tick = chartTicks[newIndex];
      setCurrentTickIndex(newIndex);

      // Update tooltip with new data
      const tooltipData = {
        date: tick[0],
        open: Number(tick[3]).toFixed(2),
        high: Number(tick[2]).toFixed(2),
        low: Number(tick[1]).toFixed(2),
        close: Number(tick[4]).toFixed(2),
        text: getPropVal(tick, "description") || "",
      };
      setCrosshairData(tooltipData);

      // Update chart highlight
      store.setSignalContextDate(tick[0]);
    }
  };

  // Add historySignals to URL parameters
  const urlParams = {
    ...(inputDate && { date: inputDate }),
    ...(historySignals.length > 0 && {
      historySignals: JSON.stringify(historySignals),
    }),
  };

  const url =
    inputDate || historySignals.length > 0
      ? store.getSymbolUrl(symbol, urlParams)
      : store.getSymbolUrl(symbol);

  // Calculate percentage change
  const ticks =
    store.currentGridMap?.[s] || store.currentGridMap?.[symbol] || [];
  const [today, prev] = ticks;
  const hasData = !!(
    today &&
    prev &&
    Number.isFinite(+today.close) &&
    Number.isFinite(+prev.close)
  );
  const close = hasData ? +today.close : null;
  const prevClose = hasData ? +prev.close : null;
  const delta = hasData ? close - prevClose : 0;
  const pct = hasData && prevClose !== 0 ? (delta / prevClose) * 100 : null;
  const hasPct = Number.isFinite(pct);

  //console.log("AAAIframe", JSON.stringify(curTick));

  const fetchNews = async () => {
    // toggle off if already open
    if (newsOpen) {
      setNewsOpen(false);
      return;
    }

    try {
      const today = store.formattedDate;
      const symbolLower = (symbol || "").toLowerCase();
      const requestJson = (url) =>
        fetch(url, {
          headers: { Accept: "application/json" },
          cache: "no-store",
        });

      const newsPromise = (async () => {
        let res = await requestJson(
          `/news_summary/${today}/${symbolLower}.json`,
        );
        if (!res.ok) {
          console.warn("Local news not found, fetching remote...");
          res = await requestJson(
            `https://options.calcfinance.net/news_summary?symbol=${symbolLower}`,
          );
        }
        return res.ok ? res.json() : null;
      })();

      const snapshotPromise = requestJson(
        `/earnings_snapshot/${today}/${symbolLower}.json`,
      )
        .then((res) => (res.ok ? res.json() : null))
        .catch((error) => {
          console.warn("Earnings snapshot unavailable", error);
          return null;
        });

      const [data, snapshot] = await Promise.all([
        newsPromise,
        snapshotPromise,
      ]);
      setNewsData(data);
      setEarningsSnapshot(snapshot);
      setNewsOpen(true);
    } catch (e) {
      console.error("fetchNews error:", e);
      setNewsData(null);
      setEarningsSnapshot(null);
      setNewsOpen(true);
    }
  };

  const buildHoursDescending = () =>
    Array.from({ length: 24 }, (_, i) => String(23 - i).padStart(2, "0"));

  const fetchOptionCache = async () => {
    if (optionsOpen) {
      setOptionsOpen(false);
      return;
    }

    setOptionsLoading(true);
    setOptionsErr(null);
    setOptionsData(null);

    try {
      const dateStr = store.formattedDate;
      const symbolLower = (symbol || "").toLowerCase();
      const hours = buildHoursDescending();
      let found = null;

      for (const hour of hours) {
        const resultsByTerm = {};

        for (const termLabel of ["long", "short"]) {
          const base = `/pv/options/${dateStr}/${termLabel}/${hour}/${symbolLower}`;
          const callUrl = `${base}.call.json`;
          const putUrl = `${base}.put.json`;

          const [callRes, putRes] = await Promise.all([
            fetch(callUrl, {
              headers: { Accept: "application/json" },
              cache: "no-store",
            }),
            fetch(putUrl, {
              headers: { Accept: "application/json" },
              cache: "no-store",
            }),
          ]);

          const callJson = callRes.ok ? await callRes.json() : null;
          const putJson = putRes.ok ? await putRes.json() : null;

          if (callJson || putJson) {
            resultsByTerm[termLabel] = { call: callJson, put: putJson };
          }
        }

        const hasLong = !!resultsByTerm.long;
        const hasShort = !!resultsByTerm.short;

        if (hasLong || hasShort) {
          found = { hour, resultsByTerm };
          const nextTerm = hasLong ? "long" : "short";
          setOptionsTerm(nextTerm);
          break;
        }
      }

      if (!found) {
        setOptionsErr("No option cache available.");
        setOptionsOpen(true);
      } else {
        setOptionsData(found);
        setOptionsOpen(true);
      }
    } catch (e) {
      console.error("fetchOptionCache error:", e);
      setOptionsErr("Failed to load option cache.");
      setOptionsOpen(true);
    }

    setOptionsLoading(false);
  };

  const getNewsUrls = () =>
    (newsData?.news || []).map((n) => n.link).filter(Boolean);

  const copyTextToClipboard = async (text) => {
    if (!text) {
      throw new Error("Nothing to copy");
    }

    if (navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(text);
        return;
      } catch (err) {
        console.warn("navigator.clipboard failed, using fallback", err);
      }
    }

    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.setAttribute("readonly", "");
    textArea.style.position = "fixed";
    textArea.style.top = "-9999px";
    textArea.style.opacity = "0";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    const copied = document.execCommand("copy");
    document.body.removeChild(textArea);

    if (!copied) {
      throw new Error("Clipboard copy failed");
    }
  };

  const handleCopyText = async (text, successMessage) => {
    try {
      await copyTextToClipboard(text);
      setToastType("success");
      setToastMsg(successMessage);
    } catch (err) {
      console.error("copy failed:", err);
      setToastType("error");
      setToastMsg("Copy failed");
    }
  };

  const handleCopyAllNewsUrls = () => {
    const urls = getNewsUrls();

    if (urls.length === 0) {
      setToastType("error");
      setToastMsg("No news URLs to copy");
      return;
    }

    handleCopyText(urls.join("\n"), `Copied ${urls.length} news URLs`);
  };

  const handleToggleFavorite = async () => {
    if (!s || favoriteSaving) return;

    if (!store.authUser) {
      setToastType("error");
      setToastMsg("Sign in required for favorites");
      return;
    }

    setFavoriteSaving(true);
    try {
      const wasFavorite = isFavorite;
      await store.toggleFavoriteList(s);
      const nowFavorite = store.favoriteList.some(
        (item) => item.toLowerCase() === s,
      );

      if (nowFavorite === wasFavorite) {
        setToastType("error");
        setToastMsg(store.favoritesStore?.lastError || "Favorite update failed");
        return;
      }

      setToastType("success");
      setToastMsg(
        `${s.toUpperCase()} ${
          wasFavorite ? "removed from" : "added to"
        } favorites`,
      );
    } catch (err) {
      console.error("toggle favorite failed:", err);
      setToastType("error");
      setToastMsg("Favorite update failed");
    } finally {
      setFavoriteSaving(false);
    }
  };

  const handleLess = () => store.setLess(!store.less);
  const handleCut = () => store.setCut(!store.cut);
  const handleHist = () => {
    const hist = getHistFromDate(inputDate || store.formattedDate);
    if (hist) {
      store.setHist(hist);
      setSearchParam("hist", hist);
    }
  };
  const handlePrev = () => {
    const prevHist = goPrevHist(store.hist);
    if (prevHist) {
      store.setHist(prevHist);
      setSearchParam("hist", prevHist);
    }
  };
  const handleNext = () => {
    const nextHist = goNextHist(store.hist);
    if (nextHist) {
      store.setHist(nextHist);
      setSearchParam("hist", nextHist);
      store.setCut(false);
    }
  };
  const handleOpenChart = () => {
    const chartWindow = window.open(url, "_blank", "noopener,noreferrer");
    if (chartWindow) chartWindow.opener = null;
  };
  const handleBuy = (e) => {
    const sector = getSectorBySymbol(symbol);
    const tick = curTick
      ? { ...curTick, symbol, lo: curTick.low, close: curTick.close, sector }
      : { symbol, sector };

    addWatch(
      {
        ticks: [
          {
            ...tick,
            pattern,
            orderType: 1,
            signalStrength: 5,
          },
        ],
        date: inputDate || store.formattedDate,
      },
      () => {
        store.addSignalSymbol({
          symbol: s,
          orderType: 1,
          sector,
          signalStrength: 5,
        });
        setToastType("success");
        setToastMsg(`Buy ${s.toUpperCase()} added`);
      },
    );
  };

  const handleSell = (e) => {
    const sector = getSectorBySymbol(symbol);
    const tick = curTick
      ? { ...curTick, symbol, lo: curTick.low, close: curTick.close, sector }
      : { symbol, sector };

    addWatch(
      {
        ticks: [
          {
            ...tick,
            pattern,
            orderType: -1,
            signalStrength: 5,
          },
        ],
        date: inputDate || store.formattedDate,
      },
      () => {
        store.addSignalSymbol({
          symbol: s,
          orderType: -1,
          sector,
          signalStrength: 5,
        });
        setToastType("success");
        setToastMsg(`Sell ${s.toUpperCase()} added`);
      },
    );
  };

  const closeToast = () => setToastMsg(null);

  return (
    <div
      style={{
        marginTop: 6,
        padding: 6,
        border: "1px solid #e5e7eb",
        borderRadius: 6,
        background: "#fafafa",
        maxWidth: `${maxWidth}px`,
        width: "100%",
        marginLeft: "auto",
        marginRight: "auto",
        ...containerStyle,
      }}
    >
      {/* Info line */}
      <div className="flex flex-wrap items-center justify-end mb2">
        {(isFinite(marketCap) || hasData) && (
          <div className="pa2 br2 bg-near-white mid-gray mr2 mb2">
            {isFinite(marketCap) ? `${Math.round(marketCap)}b` : ""}
            {isFinite(marketCap) && hasData ? " " : ""}
            {hasData ? (
              <>
                <span>${close.toFixed(2)}{hasPct ? " " : ""}</span>
                {hasPct && (
                  <span style={{ color: pct >= 0 ? "green" : "red" }}>
                    {pct >= 0 ? "+" : ""}
                    {pct.toFixed(1)}%
                  </span>
                )}
              </>
            ) : (
              ""
            )}
          </div>
        )}

        {erVal && (
          <div className="pa2 br2 bg-near-white mid-gray mr2 mb2">{erVal}</div>
        )}

        <button
          type="button"
          onClick={handleOpenChart}
          className="open-chart-button pa1 br2 gray mr2 mb2 pointer"
          style={{ border: "none", background: "transparent", minWidth: 24, opacity: 0.45 }}
          title="Open chart in new tab"
          aria-label="Open chart in new tab"
        >
          ↗
        </button>

        <div className="pa2 br2 bg-near-white mid-gray mr2 mb2">
          {getSectorsBySymbol(symbol)
            .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
            .join(", ") || "Unknown"}
        </div>

        {pattern && (
          <div
            onClick={() => setPatternExpanded(!patternExpanded)}
            className={`pa2 br2 pointer mr2 mb2 ${
              patternExpanded ? "bg-blue white" : "bg-near-white mid-gray"
            }`}
            style={{ cursor: "pointer" }}
            title={
              patternExpanded ? "Click to collapse" : `Full pattern: ${pattern}`
            }
          >
            {patternExpanded
              ? pattern
              : pattern.length > 4
                ? `...${pattern.slice(-4)}`
                : pattern}
          </div>
        )}

        <button
          type="button"
          onClick={handleToggleFavorite}
          disabled={favoriteSaving || !s}
          className={`pa2 br2 pointer mr2 mb2 ${
            isFavorite ? "bg-yellow black" : "bg-near-white mid-gray"
          }`}
          style={{
            border: "1px solid #e5e7eb",
            cursor: favoriteSaving || !s ? "not-allowed" : "pointer",
            minWidth: 40,
          }}
          title={
            isFavorite
              ? "Remove from Favorite tab"
              : "Add to Favorite tab"
          }
          aria-pressed={isFavorite}
        >
          {favoriteSaving ? "..." : isFavorite ? "★ -" : "★ +"}
        </button>

        {inSignals && (
          <div
            title={inPosSignals ? "Signal +" : "Signal -"}
            className="mr2 mb2"
            style={{
              width: 12,
              height: 12,
              borderRadius: "50%",
              backgroundColor: inPosSignals
                ? "rgba(34,197,94,.9)"
                : "rgba(239,68,68,.9)",
              boxShadow: "0 0 0 2px rgba(0,0,0,.4)",
            }}
          />
        )}

        {/* Signal history button */}
        <div
          onClick={() =>
            historySignals.length > 0 &&
            setSignalHistoryExpanded(!signalHistoryExpanded)
          }
          className={`pa2 br2 pointer mr2 mb2 ${
            historySignals.length > 0
              ? signalHistoryExpanded
                ? "bg-blue white"
                : "bg-light-gray gray"
              : "bg-light-gray black-30"
          }`}
          style={{
            cursor: historySignals.length > 0 ? "pointer" : "not-allowed",
          }}
          title={
            historySignals.length > 0
              ? `${signalHistoryExpanded ? "Hide" : "Show"} signal history (${historySignals.length})`
              : "No signal history"
          }
        >
          📊 {historySignals.length}
        </div>

        {/* Tooltip visibility checkbox */}
        <div
          className="pa2 br2 bg-near-white mid-gray mr2 mb2 flex items-center"
          style={{ gap: 4 }}
        >
          <input
            type="checkbox"
            id={`tooltip-${symbol}`}
            checked={tooltipVisible}
            onChange={() => setTooltipVisible(!tooltipVisible)}
            style={{ margin: 0 }}
          />
          <label
            htmlFor={`tooltip-${symbol}`}
            style={{ fontSize: 12, cursor: "pointer", margin: 0 }}
          >
            📊
          </label>
        </div>

        {/* Chart navigation controls - only show when tooltip is visible */}
        {tooltipVisible && (
          <>
            <div
              onClick={handleChartLeft}
              className={`pa2 br2 pointer mr2 mb2 ${
                currentTickIndex >= chartTicks.length - 1
                  ? "bg-light-gray black-30"
                  : "bg-light-gray gray"
              }`}
              style={{
                cursor:
                  currentTickIndex >= chartTicks.length - 1
                    ? "not-allowed"
                    : "pointer",
              }}
              title="Go left on chart (older dates)"
            >
              ◀
            </div>

            <div
              onClick={handleChartRight}
              className={`pa2 br2 pointer mr2 mb2 ${
                currentTickIndex <= 0
                  ? "bg-light-gray black-30"
                  : "bg-light-gray gray"
              }`}
              style={{
                cursor: currentTickIndex <= 0 ? "not-allowed" : "pointer",
              }}
              title="Go right on chart (newer dates)"
            >
              ▶
            </div>
          </>
        )}

        <div
          onClick={fetchOptionCache}
          className={`pa2 br2 pointer mr2 mb2 ${
            optionsOpen ? "bg-blue white" : "bg-light-gray gray"
          }`}
          title="Show cached option results"
        >
          {optionsLoading ? "..." : "📋"}
        </div>

        {store.isAdmin && (
          <div className="pa1 br2 bg-black-10 dark-gray mr2 mb2 f7 o-50">
            {store.hist}
          </div>
        )}

        <div
          onClick={handleHist}
          className="pa2 br2 bg-light-gray gray pointer tc mr2 mb2"
          style={{ minWidth: 40 }}
        >
          h
        </div>

        <div
          onClick={handlePrev}
          className="pa2 br2 bg-near-white mid-gray mr2 mb2"
        >
          ←
        </div>

        <div
          onClick={handleNext}
          className="pa2 br2 bg-near-white mid-gray mr2 mb2"
        >
          →
        </div>
      </div>

      {/* Control line - frequently used controls */}
      <div
        className="flex flex-wrap items-center justify-end mb2"
        style={{ gap: 8 }}
      >
        {store.isAdminParam && (
          <div
            onClick={handleBuy}
            className="pa2 br2 bg-light-gray gray pointer mb2"
            title="Buy signal"
          >
            B
          </div>
        )}

        {store.isAdminParam && (
          <div
            onClick={handleSell}
            className="pa2 br2 bg-light-gray gray pointer mb2"
            title="Sell signal"
          >
            S
          </div>
        )}

        <div
          onClick={() => store.setActiveTab("watch_all")}
          className="pa2 br2 bg-light-gray gray pointer mb2"
          title="Open watchlists"
        >
          W
        </div>

        <div className="mr2 mb2">
          <NewsSummaryButton symbol={symbol} onFetch={fetchNews} />
        </div>
        {/* Admin/Percentage toggle */}
        {/* 
        <button
          onClick={() => store.setIsPercent(!store.isPercent)}
          className="pa2 mb2 link"
          style={{
            cursor: "pointer",
            fontSize: 12,
            fontWeight: 600,
            border: "none",
            background: "transparent",
            color: "#666",
          }}
          title={
            store.isPercent
              ? "Switch to pattern view"
              : "Switch to percentage view"
          }
        >
          %
        </button>
        */}

        <div
          onClick={handleLess}
          className={`pa2 br2 pointer mb2 bg-light-gray tc ${
            store.less ? "gray" : "black"
          }`}
          style={{ minWidth: 40 }}
        >
          z
        </div>

        <div
          onClick={handleCut}
          className={`pa2 br2 pointer mb2 bg-light-gray tc ${
            store.cut ? "gray" : "black"
          }`}
          style={{ minWidth: 40 }}
        >
          *
        </div>
      </div>

      {/* Signal history expanded display */}
      {signalHistoryExpanded && historySignals.length > 0 && (
        <div
          className="mb2 pa3 br2 bg-light-yellow black-80"
          style={{
            fontSize: 12,
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
            border: "1px solid rgba(0,0,0,0.1)",
          }}
        >
          <div style={{ fontWeight: "bold", marginBottom: 8, color: "#666" }}>
            Signal History ({historySignals.length})
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {historySignals.map((signal, index) => (
              <div
                key={`${signal.date}-${signal.type}-${index}`}
                style={{
                  padding: "4px 8px",
                  borderRadius: 4,
                  border: "1px solid rgba(0,0,0,0.1)",
                  background:
                    signal.type === "Buy"
                      ? "rgba(34,197,94,0.1)"
                      : "rgba(239,68,68,0.1)",
                  color: signal.type === "Buy" ? "darkgreen" : "darkred",
                  fontSize: 11,
                }}
              >
                <span style={{ marginRight: 4 }}>
                  {signal.type === "Buy" ? "🟩" : "🟥"}
                </span>
                <span style={{ fontWeight: "bold" }}>{signal.type}</span>
                {signal.date && (
                  <span style={{ marginLeft: 4, color: "#666" }}>
                    {formatDate(signal.date)}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Crosshair tooltip display - conditional */}
      {tooltipVisible && (
        <div
          className="mb2 pa2"
          style={{
            fontSize: 12,
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
            minHeight: 24,
          }}
        >
          {crosshairData ? (
            <>
              <span style={{ color: "#999", fontWeight: "normal" }}>
                {crosshairData.date}
              </span>{" "}
              <span style={{ color: "#aaa" }}>
                C{crosshairData.close} L{crosshairData.low}
              </span>
              {crosshairData.text && (
                <span
                  style={{ color: "#bbb", fontWeight: "normal", marginLeft: 4 }}
                >
                  {crosshairData.text}
                </span>
              )}{" "}
              <span style={{ color: "#ccc" }}>
                O{crosshairData.open} H{crosshairData.high}
              </span>
            </>
          ) : (
            <span style={{ color: "transparent" }}>&nbsp;</span>
          )}
        </div>
      )}

      {newsOpen && (
        <div
          className="mb3 bg-near-white br2 ba b--black-10 shadow-4 pa3"
          style={{
            width: "100%",
            marginBottom: 12,
          }}
        >
          {newsData || earningsSnapshot?.hasEarningsData ? (
            <>
              <div className="mb2 f6 dark-gray flex items-center justify-between">
                <div>
                  <strong className="black">
                    {newsData?.symbol || earningsSnapshot?.symbol || symbol}
                  </strong>{" "}
                </div>
                {/*<span className="mid-gray">— {newsData.summary}</span>*/}
                <button
                  type="button"
                  onClick={handleCopyAllNewsUrls}
                  className="pointer bg-white dark-gray"
                  style={copyButtonStyle}
                  title="Copy all news URLs"
                  aria-label="Copy all news URLs"
                  disabled={getNewsUrls().length === 0}
                >
                  ⧉
                </button>
              </div>
              <EarningsSnapshot data={earningsSnapshot} />
              {earningsSnapshot?.hasEarningsData && newsData && (
                <div
                  className="f7 mid-gray"
                  style={{
                    borderTop: "1px solid #dbe2ea",
                    marginTop: 14,
                    paddingTop: 10,
                    textTransform: "uppercase",
                  }}
                >
                  Related news
                </div>
              )}
              <ul className="list pl3 mv2 f6">
                {(newsData?.news || []).map((n, i) => (
                  <li
                    key={i}
                    className="mb2 flex items-start"
                    style={{ gap: 8 }}
                  >
                    <div style={{ flex: "1 1 auto", minWidth: 0 }}>
                      <a
                        href={n.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="link blue underline-hover"
                      >
                        {n.title}
                      </a>{" "}
                      <span className="mid-gray">
                        (
                        {n.pubDate
                          ? new Date(n.pubDate).toLocaleDateString()
                          : ""}
                        )
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() =>
                        handleCopyText(n.link, "Copied news URL")
                      }
                      className="pointer bg-white dark-gray"
                      style={copyButtonStyle}
                      title="Copy news URL"
                      aria-label="Copy news URL"
                      disabled={!n.link}
                    >
                      ⧉
                    </button>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <div className="f6 mid-gray">No news available.</div>
          )}
        </div>
      )}

      {optionsOpen && (
        <div
          className="mb3 bg-near-white br2 ba b--black-10 shadow-4 pa3"
          style={{ width: "100%", marginBottom: 12 }}
        >
          {optionsErr ? (
            <div className="f6 mid-gray">{optionsErr}</div>
          ) : (
            <OptionsCacheTable
              optionsData={optionsData}
              formattedDate={store.formattedDate}
              termLabel={optionsTerm}
              onTermChange={setOptionsTerm}
            />
          )}
        </div>
      )}
      {/* 
      <Iframe
        url={url}
        width="100%"
        height={`${height}px`}
        id={`iframe-${symbol}`}
        className={className}
        display="initial"
        position="relative"
        allowFullScreen={false}
      />
      */}
      <SymbolChart
        symbol={symbol}
        date={inputDate}
        historySignals={historySignals}
        onCrosshairMove={handleCrosshairMove}
      />

      {toastMsg && (
        <Toast
          message={toastMsg}
          type={toastType}
          onClose={closeToast}
          duration={1500}
        />
      )}
    </div>
  );
});

const divStyle = {
  padding: "4px 10px",
  fontSize: "1.25rem",
  background: "#f9fafb",
  cursor: "pointer",
};

const btnStyle = {
  border: "1px solid #d1d5db",
  borderRadius: 4,
  minWidth: "40px",
  ...divStyle,
};

const copyButtonStyle = {
  border: "1px solid #d1d5db",
  borderRadius: 4,
  minWidth: 28,
  height: 24,
  padding: "0 6px",
  lineHeight: "22px",
  fontSize: 14,
};

export default SymbolIframe;
